import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Chip,
  Tabs,
  Tab,
  Alert,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CalendarViewWeek as CalendarViewWeekIcon,
  Event as EventIcon,
  InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';
import { format, parse, startOfWeek, getDay, addMinutes } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { consultasIniciais, pacientesIniciais, psicologosIniciais } from '../shared/initialData';

// Configuração do localizador para o calendário
const locales = {
  'pt-BR': ptBR
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales
});

// Componente principal da Agenda do Paciente
const AgendaPaciente = () => {
  const [events, setEvents] = useState([]);
  const [openAgendarDialog, setOpenAgendarDialog] = useState(false);
  const [openConsultaInfoDialog, setOpenConsultaInfoDialog] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [pacienteId, setPacienteId] = useState(null);
  const [psicologoId, setPsicologoId] = useState(null);
  const [psicologoData, setPsicologoData] = useState(null);
  const [horariosDisponiveis, setHorariosDisponiveis] = useState([]);
  const [calendarView, setCalendarView] = useState('week');
  const [formErrors, setFormErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState(null);
  
  // Formulário para agendar consulta
  const [formData, setFormData] = useState({
    data: null,
    horarioSelecionado: null,
    tipo: 'Presencial',
    plataforma: '',
    observacoes: ''
  });

  // Simulação de dados dos dias e horários disponíveis do psicólogo
  const [horariosFuncionamento, setHorariosFuncionamento] = useState({
    segunda: { ativo: true, inicio: '08:00', fim: '18:00' },
    terca: { ativo: true, inicio: '08:00', fim: '18:00' },
    quarta: { ativo: true, inicio: '08:00', fim: '18:00' },
    quinta: { ativo: true, inicio: '08:00', fim: '18:00' },
    sexta: { ativo: true, inicio: '08:00', fim: '18:00' },
    sabado: { ativo: false, inicio: '08:00', fim: '12:00' },
    domingo: { ativo: false, inicio: '00:00', fim: '00:00' }
  });
  
  // Simulação de dias bloqueados
  const [diasBloqueados, setDiasBloqueados] = useState([]);

  // Carrega dados iniciais
  useEffect(() => {
    // Verifica se o usuário é paciente
    const tipoUsuario = localStorage.getItem('userType');
    if (tipoUsuario !== 'paciente') {
      return;
    }

    const storedPacienteId = localStorage.getItem('userId');
    if (storedPacienteId) {
      const pacienteIdInt = parseInt(storedPacienteId);
      setPacienteId(pacienteIdInt);
      
      // Encontra o psicólogo deste paciente
      const paciente = pacientesIniciais.find(p => p.id === pacienteIdInt);
      if (paciente) {
        const psicologoIdInt = paciente.psicologoId;
        setPsicologoId(psicologoIdInt);
        
        // Carrega dados do psicólogo
        const psicologo = psicologosIniciais.find(p => p.id === psicologoIdInt);
        if (psicologo) {
          setPsicologoData(psicologo);
          
          // Em uma aplicação real, aqui você carregaria os horários de funcionamento
          // e dias bloqueados do psicólogo a partir do backend
        }
        
        // Filtra consultas do paciente
        const consultasPaciente = consultasIniciais.filter(
          consulta => consulta.pacienteId === pacienteIdInt
        );
        
        // Converte consultas para eventos do calendário
        const eventosConsultas = consultasPaciente.map(consulta => {
          const dataHoraInicio = parseDateTime(consulta.data, consulta.horario);
          const dataHoraFim = addMinutes(dataHoraInicio, parseInt(consulta.duracao));
          
          return {
            id: consulta.id,
            title: `Consulta ${consulta.tipo}`,
            start: dataHoraInicio,
            end: dataHoraFim,
            tipo: consulta.tipo,
            plataforma: consulta.plataforma,
            status: consulta.status,
            observacoes: consulta.observacoes,
            resource: { status: consulta.status, tipo: 'consulta' }
          };
        });
        
        // Consultas do psicólogo com outros pacientes (bloqueadas para este paciente)
        const consultasOutrosPacientes = consultasIniciais.filter(
          consulta => consulta.psicologoId === psicologoIdInt && 
                      consulta.pacienteId !== pacienteIdInt
        );
        
        const eventosOutrosPacientes = consultasOutrosPacientes.map(consulta => {
          const dataHoraInicio = parseDateTime(consulta.data, consulta.horario);
          const dataHoraFim = addMinutes(dataHoraInicio, parseInt(consulta.duracao));
          
          return {
            id: `bloqueado-${consulta.id}`,
            title: "Horário Indisponível",
            start: dataHoraInicio,
            end: dataHoraFim,
            resource: { tipo: 'bloqueado' }
          };
        });
        
        // Combina todos os eventos
        setEvents([...eventosConsultas, ...eventosOutrosPacientes]);
      }
    }
  }, []);

  // Função para converter string de data e hora para objeto Date
  const parseDateTime = (dataStr, horaStr) => {
    const [dia, mes, ano] = dataStr.split('/');
    const [hora, minuto] = horaStr.split(':');
    return new Date(ano, mes - 1, dia, hora, minuto);
  };

  // Manipuladores para o calendário
  const handleSelectSlot = ({ start, end }) => {
    // Verifica se é um dia/horário válido para agendamento
    const dataFormatada = format(start, 'yyyy-MM-dd');
    if (diasBloqueados.includes(dataFormatada)) {
      setAlertMessage({
        severity: 'warning',
        message: 'Este dia não está disponível para agendamento.'
      });
      return;
    }
    
    // Verifica se está dentro do horário de funcionamento
    const diaSemana = getDayName(start.getDay());
    const horarioFunc = horariosFuncionamento[diaSemana];
    
    if (!horarioFunc.ativo) {
      setAlertMessage({
        severity: 'warning',
        message: 'Não há atendimento neste dia da semana.'
      });
      return;
    }
    
    const horaInicio = start.getHours() + ':' + (start.getMinutes() < 10 ? '0' : '') + start.getMinutes();
    if (horaInicio < horarioFunc.inicio || horaInicio >= horarioFunc.fim) {
      setAlertMessage({
        severity: 'warning',
        message: 'Este horário está fora do período de atendimento.'
      });
      return;
    }
    
    // Verifica se não há sobreposição com outros eventos
    const sobreposicao = events.find(event => {
      return (
        (start >= event.start && start < event.end) ||
        (end > event.start && end <= event.end) ||
        (start <= event.start && end >= event.end)
      );
    });
    
    if (sobreposicao) {
      setAlertMessage({
        severity: 'warning',
        message: 'Este horário já está ocupado.'
      });
      return;
    }
    
    // Se tudo estiver ok, abre o diálogo de agendamento
    const data = new Date(start);
    const dataFormatadaForm = format(data, 'yyyy-MM-dd');
    
    // Gera horários disponíveis em intervalos de 30 minutos
    const horariosDisp = gerarHorariosDisponiveis(start, horarioFunc);
    setHorariosDisponiveis(horariosDisp);
    
    setFormData({
      data: dataFormatadaForm,
      horarioSelecionado: format(start, 'HH:mm'),
      tipo: 'Presencial',
      plataforma: '',
      observacoes: ''
    });
    
    setOpenAgendarDialog(true);
  };

  const handleSelectEvent = (event) => {
    // Se for uma consulta do paciente, mostra detalhes
    if (event.resource && event.resource.tipo === 'consulta') {
      setCurrentEvent(event);
      setOpenConsultaInfoDialog(true);
    } else {
      // Se for um horário bloqueado, apenas informa
      setAlertMessage({
        severity: 'info',
        message: 'Este horário não está disponível para agendamento.'
      });
    }
  };

  // Gerar horários disponíveis para um dia específico
  const gerarHorariosDisponiveis = (data, horarioFunc) => {
    const horarios = [];
    
    // Cria horários a cada 30 minutos entre início e fim
    const [horaInicio, minInicio] = horarioFunc.inicio.split(':').map(Number);
    const [horaFim, minFim] = horarioFunc.fim.split(':').map(Number);
    
    const inicioMinutos = horaInicio * 60 + minInicio;
    const fimMinutos = horaFim * 60 + minFim;
    
    for (let minutos = inicioMinutos; minutos < fimMinutos; minutos += 30) {
      const hora = Math.floor(minutos / 60);
      const min = minutos % 60;
      
      const horarioStr = `${hora.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      
      // Verifica se este horário não coincide com eventos existentes
      const horarioData = new Date(data);
      horarioData.setHours(hora);
      horarioData.setMinutes(min);
      
      const horarioFim = new Date(horarioData);
      horarioFim.setMinutes(horarioFim.getMinutes() + 50); // Consultas de 50 minutos
      
      const sobreposicao = events.find(event => {
        return (
          (horarioData >= event.start && horarioData < event.end) ||
          (horarioFim > event.start && horarioFim <= event.end) ||
          (horarioData <= event.start && horarioFim >= event.end)
        );
      });
      
      if (!sobreposicao) {
        horarios.push(horarioStr);
      }
    }
    
    return horarios;
  };

  // Manipuladores para os diálogos
  const handleCloseAgendarDialog = () => {
    setOpenAgendarDialog(false);
    setFormErrors({});
  };

  const handleCloseConsultaInfoDialog = () => {
    setOpenConsultaInfoDialog(false);
    setCurrentEvent(null);
  };

  // Manipuladores de formulário
  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
    
    // Limpa o erro quando o usuário corrige o campo
    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: null
      });
    }
  };

  // Validação e salvamento
  const validateForm = () => {
    const errors = {};
    
    if (!formData.horarioSelecionado) {
      errors.horarioSelecionado = 'Selecione um horário';
    }
    
    if (formData.tipo === 'Online' && !formData.plataforma) {
      errors.plataforma = 'Informe a plataforma para consulta online';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveAgendamento = () => {
    if (!validateForm()) return;
    
    // Criar objeto de data/hora
    const [ano, mes, dia] = formData.data.split('-');
    const [hora, minuto] = formData.horarioSelecionado.split(':');
    
    const dataInicio = new Date(ano, mes - 1, dia, hora, minuto);
    const dataFim = addMinutes(dataInicio, 50); // Consulta de 50 minutos
    
    // Criar novo evento
    const novoEvento = {
      id: Math.max(...events.map(e => typeof e.id === 'number' ? e.id : 0), 0) + 1,
      title: `Consulta ${formData.tipo}`,
      start: dataInicio,
      end: dataFim,
      tipo: formData.tipo,
      plataforma: formData.plataforma,
      status: 'Agendada', // Status inicial
      observacoes: formData.observacoes,
      pacienteId: pacienteId,
      psicologoId: psicologoId,
      resource: { status: 'Agendada', tipo: 'consulta' }
    };
    
    // Adicionar evento ao calendário
    setEvents([...events, novoEvento]);
    
    // Em uma aplicação real, aqui você enviaria o agendamento para o backend
    
    // Mostrar mensagem de sucesso
    setAlertMessage({
      severity: 'success',
      message: 'Consulta agendada com sucesso!'
    });
    
    handleCloseAgendarDialog();
  };

  const handleCancelarConsulta = () => {
    if (currentEvent && window.confirm('Tem certeza que deseja cancelar esta consulta?')) {
      // Em uma aplicação real, você enviaria este cancelamento para o backend
      
      // Atualiza o status da consulta no frontend
      const eventosAtualizados = events.map(evento => {
        if (evento.id === currentEvent.id) {
          return {
            ...evento,
            status: 'Cancelada',
            resource: { ...evento.resource, status: 'Cancelada' }
          };
        }
        return evento;
      });
      
      setEvents(eventosAtualizados);
      setAlertMessage({
        severity: 'success',
        message: 'Consulta cancelada com sucesso!'
      });
      
      handleCloseConsultaInfoDialog();
    }
  };

  // Funções auxiliares
  const getDayName = (dayIndex) => {
    const days = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
    return days[dayIndex];
  };

  // Estilizações para o calendário
  const eventStyleGetter = (event) => {
    let backgroundColor = '#4a8a84';
    let textColor = 'white';
    
    if (event.resource && event.resource.tipo === 'bloqueado') {
      backgroundColor = '#f5f5f5';
      textColor = '#757575';
    } else if (event.status) {
      switch (event.status) {
        case 'Agendada':
          backgroundColor = '#4a8a84';
          break;
        case 'Realizada':
          backgroundColor = '#4caf50';
          break;
        case 'Cancelada':
          backgroundColor = '#9e9e9e';
          textColor = '#333';
          break;
        case 'Remarcada':
          backgroundColor = '#ff9800';
          break;
        default:
          backgroundColor = '#4a8a84';
      }
    }
    
    return {
      style: {
        backgroundColor,
        color: textColor,
        borderRadius: '4px',
        border: 'none',
        padding: '2px 5px'
      }
    };
  };

  const dayPropGetter = (date) => {
    const dataFormatada = format(date, 'yyyy-MM-dd');
    const diaSemana = getDayName(date.getDay());
    const horarioFunc = horariosFuncionamento[diaSemana];
    
    if (!horarioFunc.ativo || diasBloqueados.includes(dataFormatada)) {
      return {
        style: {
          backgroundColor: '#f5f5f5',
          opacity: 0.7
        }
      };
    }
    
    return {};
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }}>
        <Typography variant="h4">
          Minhas Consultas
        </Typography>
      </Box>
      
      {psicologoData && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Seu Psicólogo
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">
                {psicologoData.nome}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CRP: {psicologoData.crp} • {psicologoData.especialidade}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Valor da consulta: R$ {psicologoData.valorConsulta}
              </Typography>
            </Box>
            <Button 
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                const hoje = new Date();
                handleSelectSlot({ 
                  start: new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 9, 0),
                  end: new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 10, 0)
                });
              }}
              sx={{ bgcolor: '#4a8a84', '&:hover': { bgcolor: '#3a7a74' } }}
            >
              Agendar Consulta
            </Button>
          </Box>
        </Paper>
      )}
      
      {alertMessage && (
        <Alert 
          severity={alertMessage.severity} 
          sx={{ mb: 3 }}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.message}
        </Alert>
      )}
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}>
          <Tabs
            value={calendarView}
            onChange={(e, newValue) => setCalendarView(newValue)}
            sx={{
              '& .MuiTab-root': { minWidth: 100 },
              '& .Mui-selected': { color: '#4a8a84' },
              '& .MuiTabs-indicator': { backgroundColor: '#4a8a84' }
            }}
          >
            <Tab value="day" label="Dia" icon={<EventIcon />} iconPosition="start" />
            <Tab value="week" label="Semana" icon={<CalendarViewWeekIcon />} iconPosition="start" />
            <Tab value="month" label="Mês" icon={<CalendarViewWeekIcon />} iconPosition="start" />
          </Tabs>
          
          <Typography variant="body2" color="text.secondary">
            {events.filter(e => e.resource && e.resource.tipo === 'consulta').length} consultas no total
          </Typography>
        </Box>
        
        <Box sx={{ height: 600 }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            views={['day', 'week', 'month']}
            view={calendarView}
            onView={(view) => setCalendarView(view)}
            selectable
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
            dayPropGetter={dayPropGetter}
            toolbar={true}
            messages={{
              today: 'Hoje',
              previous: 'Anterior',
              next: 'Próximo',
              month: 'Mês',
              week: 'Semana',
              day: 'Dia',
              agenda: 'Agenda',
              date: 'Data',
              time: 'Hora',
              event: 'Evento',
              noEventsInRange: 'Não há eventos neste período',
              showMore: (total) => `+ ${total} mais`
            }}
            formats={{
              dateFormat: 'dd',
              dayFormat: 'dd ddd',
              monthHeaderFormat: 'MMMM yyyy',
              dayHeaderFormat: 'dddd, DD MMMM',
              dayRangeHeaderFormat: ({ start, end }) => 
                `${format(start, 'dd/MM/yyyy')} - ${format(end, 'dd/MM/yyyy')}`
            }}
            min={new Date(0, 0, 0, 7, 0, 0)}
            max={new Date(0, 0, 0, 21, 0, 0)}
          />
        </Box>
        
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
          <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Clique em um horário vazio para agendar uma nova consulta
          </Typography>
        </Box>
      </Paper>
      
      {/* Dialog para agendar consulta */}
      <Dialog open={openAgendarDialog} onClose={handleCloseAgendarDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Agendar Nova Consulta</DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data"
                type="date"
                value={formData.data || ''}
                onChange={(e) => {
                  handleInputChange('data', e.target.value);
                  // Recalcular horários disponíveis aqui
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.horarioSelecionado}>
                <InputLabel>Horário</InputLabel>
                <Select
                  value={formData.horarioSelecionado || ''}
                  label="Horário"
                  onChange={(e) => handleInputChange('horarioSelecionado', e.target.value)}
                >
                  {horariosDisponiveis.length > 0 ? (
                    horariosDisponiveis.map((horario) => (
                      <MenuItem key={horario} value={horario}>
                        {horario}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      Sem horários disponíveis
                    </MenuItem>
                  )}
                </Select>
                {formErrors.horarioSelecionado && (
                  <Typography variant="caption" color="error">
                    {formErrors.horarioSelecionado}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Consulta</InputLabel>
                <Select
                  value={formData.tipo}
                  label="Tipo de Consulta"
                  onChange={(e) => handleInputChange('tipo', e.target.value)}
                >
                  <MenuItem value="Presencial">Presencial</MenuItem>
                  <MenuItem value="Online">Online</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Plataforma (se online)"
                value={formData.plataforma || ''}
                onChange={(e) => handleInputChange('plataforma', e.target.value)}
                disabled={formData.tipo !== 'Online'}
                error={!!formErrors.plataforma}
                helperText={formErrors.plataforma}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações ou assuntos a abordar"
                multiline
                rows={3}
                value={formData.observacoes || ''}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                O agendamento está sujeito à confirmação do psicólogo.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseAgendarDialog}>Cancelar</Button>
          <Button 
            onClick={handleSaveAgendamento} 
            variant="contained"
            sx={{ bgcolor: '#4a8a84', '&:hover': { bgcolor: '#3a7a74' } }}
          >
            Confirmar Agendamento
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog para detalhes da consulta */}
      <Dialog open={openConsultaInfoDialog} onClose={handleCloseConsultaInfoDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Detalhes da Consulta</DialogTitle>
        
        <DialogContent>
          {currentEvent && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Chip 
                  label={currentEvent.status} 
                  color={
                    currentEvent.status === 'Agendada' ? 'primary' : 
                    currentEvent.status === 'Realizada' ? 'success' : 
                    currentEvent.status === 'Cancelada' ? 'default' : 
                    'warning'
                  }
                  sx={{ 
                    mb: 2,
                    bgcolor: currentEvent.status === 'Agendada' ? '#4a8a84' : undefined
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Data
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {format(currentEvent.start, 'dd/MM/yyyy')}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Horário
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {format(currentEvent.start, 'HH:mm')} - {format(currentEvent.end, 'HH:mm')}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Tipo
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {currentEvent.tipo}
                </Typography>
              </Grid>
              
              {currentEvent.tipo === 'Online' && currentEvent.plataforma && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Plataforma
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {currentEvent.plataforma}
                  </Typography>
                </Grid>
              )}
              
              {currentEvent.observacoes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Observações
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {currentEvent.observacoes}
                  </Typography>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {currentEvent.status === 'Agendada' ? (
                    "Você pode cancelar esta consulta até 24 horas antes do horário agendado."
                  ) : currentEvent.status === 'Cancelada' ? (
                    "Esta consulta foi cancelada."
                  ) : (
                    "Esta consulta já foi realizada."
                  )}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions>
          {currentEvent && currentEvent.status === 'Agendada' && (
            <Button 
              onClick={handleCancelarConsulta} 
              color="error"
              sx={{ mr: 'auto' }}
            >
              Cancelar Consulta
            </Button>
          )}
          <Button onClick={handleCloseConsultaInfoDialog}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgendaPaciente; 